import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Footprints, Zap, Trophy, ChevronRight, Check, X } from "lucide-react";
import { Button } from "./ui/button";
import { useTranslation } from "react-i18next";
import { isHealthSupported } from "../lib/health";
import { setStepsOptIn } from "../lib/stepsOptIn";

// Three swipeable / tap-through onboarding cards shown once after first login.
// Dismissed flag stored in localStorage so returning users never see it again.
const STORAGE_KEY = "fitjong_welcome_seen_v1";

const SLIDE_META = [
  { id: "match", icon: Trophy, bg: "linear-gradient(135deg,#EDE9DA 0%,#D7CFB6 100%)", accent: "#7A5F2B" },
  { id: "energy", icon: Zap, bg: "linear-gradient(135deg,#E2EAE3 0%,#B6CDB8 100%)", accent: "#3D5942" },
  { id: "streak", icon: Footprints, bg: "linear-gradient(135deg,#F1E5D6 0%,#E2C49D 100%)", accent: "#8C5F2B" },
];

export default function WelcomeCarousel({ open, onClose }) {
  const { t } = useTranslation();
  const [idx, setIdx] = useState(0);

  const SLIDES = SLIDE_META.map((s, i) => ({
    ...s,
    title: t(`welcome.slide${i + 1}_title`),
    body: t(`welcome.slide${i + 1}_body`),
  }));
  // Iter73 — Steps opt-in question slide. Only injected on Android
  // (Health Connect platform) so users who can't sync steps anyway
  // aren't bothered with an irrelevant question.
  const stepsSlideEnabled = isHealthSupported();
  if (stepsSlideEnabled) {
    SLIDES.push({
      id: "steps_opt_in",
      icon: Footprints,
      bg: "linear-gradient(135deg,#E2EAE3 0%,#A8C5AC 100%)",
      accent: "#3D5942",
      title: t("welcome.steps_optin_title"),
      body: t("welcome.steps_optin_body"),
      isChoice: true,
    });
  }

  useEffect(() => {
    if (!open) setIdx(0);
  }, [open]);

  if (!open) return null;

  const slide = SLIDES[idx];
  const isLast = idx === SLIDES.length - 1;
  const next = () => {
    if (isLast) {
      try { localStorage.setItem(STORAGE_KEY, "1"); } catch (_) {}
      onClose();
    } else {
      setIdx((i) => i + 1);
    }
  };
  const skip = () => {
    try { localStorage.setItem(STORAGE_KEY, "1"); } catch (_) {}
    onClose();
  };

  return (
    <div
      data-testid="welcome-carousel"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-sm max-h-[90vh] overflow-y-auto bg-[#F7F5F0] rounded-3xl overflow-hidden shadow-2xl"
      >
        <div
          className="relative h-40 sm:h-56 flex items-center justify-center"
          style={{ background: slide.bg }}
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={slide.id}
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -30 }}
              transition={{ duration: 0.25 }}
              className="flex flex-col items-center"
            >
              <slide.icon size={72} strokeWidth={1.4} style={{ color: slide.accent }} />
            </motion.div>
          </AnimatePresence>
          <button
            type="button"
            data-testid="welcome-skip"
            onClick={skip}
            className="absolute top-3 right-4 text-xs text-[#2C332A]/60 hover:text-[#2C332A]"
          >
            {t("welcome.skip")}
          </button>
        </div>
        <div className="p-6 text-center">
          <AnimatePresence mode="wait">
            <motion.div
              key={slide.id + "-text"}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.25 }}
            >
              <h2
                style={{ fontFamily: "Outfit, sans-serif" }}
                className="text-2xl font-light tracking-tight text-[#2C332A] mb-2"
              >
                {slide.title}
              </h2>
              <p className="text-sm text-[#2C332A]/70 leading-relaxed mb-6">
                {slide.body}
              </p>
            </motion.div>
          </AnimatePresence>

          <div className="flex items-center justify-center gap-1.5 mb-5">
            {SLIDES.map((s, i) => (
              <button
                key={s.id}
                type="button"
                data-testid={`welcome-dot-${i}`}
                onClick={() => setIdx(i)}
                aria-label={`Slide ${i + 1}`}
                className={`h-1.5 rounded-full transition-all ${
                  i === idx ? "w-6 bg-[#5F7161]" : "w-1.5 bg-[#C9C5B7]"
                }`}
              />
            ))}
          </div>

          <Button
            data-testid="welcome-next-btn"
            onClick={next}
            className={`w-full h-12 rounded-full bg-[#5F7161] hover:bg-[#4D5D4F] text-white ${slide.isChoice ? "hidden" : ""}`}
          >
            {isLast ? t("welcome.lets_go") : t("welcome.next")}
            <ChevronRight size={18} strokeWidth={1.6} className="ml-1" />
          </Button>

          {/* Iter73 — Yes/No CTA pair for the steps opt-in slide. */}
          {slide.isChoice && (
            <div className="flex flex-col gap-2" data-testid="welcome-steps-optin">
              <Button
                data-testid="welcome-steps-yes"
                onClick={() => { setStepsOptIn("yes"); next(); }}
                className="w-full h-12 rounded-full bg-[#5F7161] hover:bg-[#4D5D4F] text-white"
              >
                <Check size={16} strokeWidth={1.8} className="mr-1.5 shrink-0" />
                {t("welcome.steps_optin_yes")}
              </Button>
              <Button
                data-testid="welcome-steps-no"
                variant="outline"
                onClick={() => { setStepsOptIn("no"); next(); }}
                className="w-full h-12 rounded-full border-[#C9C5B7] text-[#2C332A]"
              >
                <X size={16} strokeWidth={1.6} className="mr-1.5 shrink-0" />
                {t("welcome.steps_optin_no")}
              </Button>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}

export function hasSeenWelcome() {
  try { return localStorage.getItem(STORAGE_KEY) === "1"; } catch (_) { return false; }
}
